import { ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useWebSocket } from "../contexts/WebSocketContext";
import {
  LayoutDashboard,
  Ticket,
  Plus,
  LogOut,
  Bell,
  Search,
  Wrench,
  Shield,
  Command,
  ChevronRight,
  BarChart3,
  Users,
  Building2,
  FolderOpen,
  Wifi,
  Settings,
} from "lucide-react";
import { useState } from "react";
import logo from "../assets/logo.svg";
import ThemeToggle from "./ThemeToggle";

interface DashboardLayoutProps {
  children: ReactNode;
}

export default function DashboardLayout({ children }: DashboardLayoutProps) {
  const { user, logout } = useAuth();
  const { isConnected, unreadCount } = useWebSocket();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const navigation = [
    {
      name: "Service Desk",
      href: "/dashboard",
      icon: LayoutDashboard,
      roles: ["user", "technician"],
    },
    {
      name: "Create Incident",
      href: "/dashboard/new-ticket",
      icon: Plus,
      roles: ["user", "technician", "admin"],
    },
    {
      name: "My Tickets",
      href: "/dashboard/history",
      icon: Ticket,
      roles: ["user"],
    },
    {
      name: "Technician Queue",
      href: "/dashboard/technician",
      icon: Wrench,
      roles: ["technician", "admin"],
    },
    {
      name: "Administration",
      href: "/dashboard/admin",
      icon: Shield,
      roles: ["admin"],
    },
    {
      name: "Reports",
      href: "/dashboard/reports",
      icon: BarChart3,
      roles: ["admin"],
    },
    {
      name: "Users",
      href: "/dashboard/users",
      icon: Users,
      roles: ["admin"],
    },
    {
      name: "Departments",
      href: "/dashboard/departments",
      icon: Building2,
      roles: ["admin"],
    },
    {
      name: "Categories",
      href: "/dashboard/categories",
      icon: FolderOpen,
      roles: ["admin"],
    },
    {
      name: "Settings",
      href: "/dashboard/notification-settings",
      icon: Settings,
      roles: ["user", "technician", "admin"],
    },
  ];

  const filteredNav = navigation.filter((item) =>
    item.roles.includes(user?.role || "user"),
  );

  return (
    <div className="min-h-screen bg-background flex">
      <aside
        className={`${
          sidebarCollapsed ? "w-20" : "w-72"
        } border-r border-border/50 bg-card transition-all duration-300 flex-col fixed lg:sticky top-0 h-screen z-50 ${
          sidebarCollapsed ? "hidden lg:flex" : "flex"
        }`}
      >
        {!sidebarCollapsed && (
          <div
            className="fixed inset-0 bg-black/50 lg:hidden -z-10"
            onClick={() => setSidebarCollapsed(true)}
          />
        )}
        <div className="h-16 sm:h-20 border-b border-border/50 flex items-center px-4 sm:px-6">
          <Link to="/dashboard" className="flex items-center gap-3 flex-1">
            <div className="relative">
              <div className="absolute inset-0 bg-primary/20 blur-lg animate-pulse" />
              <div className="relative w-16 h-16 flex items-center justify-center">
                <img src={logo} alt="CamPulse logo" className="w-16 h-16" />
              </div>
            </div>
            {!sidebarCollapsed && (
              <div>
                <h2 className="font-bold text-foreground tracking-tight">
                  <span className="text-primary font-semibold">Cam</span>
                  <span className="font-light">Pulse</span>
                </h2>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">
                  Service Desk
                </p>
              </div>
            )}
          </Link>
        </div>

        <nav className="flex-1 p-3 sm:p-4 space-y-1.5 sm:space-y-2 overflow-y-auto">
          {filteredNav.map((item) => {
            const isActive =
              location.pathname === item.href ||
              (item.href !== "/dashboard" &&
                location.pathname.startsWith(item.href));
            const Icon = item.icon;
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setSidebarCollapsed(true)}
                className={`relative group flex items-center gap-3 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg text-sm font-medium transition-all ${
                  isActive
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                }`}
              >
                {isActive && (
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-primary rounded-r-full" />
                )}
                <Icon className="w-5 h-5 flex-shrink-0" />
                {!sidebarCollapsed && <span>{item.name}</span>}
                {!sidebarCollapsed && (
                  <ChevronRight
                    className={`w-4 h-4 ml-auto opacity-0 group-hover:opacity-100 transition-all ${
                      isActive ? "opacity-100" : ""
                    }`}
                  />
                )}
              </Link>
            );
          })}
        </nav>

        <div className="border-t border-border/50 p-4 space-y-2">
          <div
            className={`flex items-center gap-3 px-4 py-3 rounded-lg bg-muted/50 ${
              sidebarCollapsed ? "justify-center" : ""
            }`}
          >
            <div className="relative flex-shrink-0">
              <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                <span className="text-xs font-bold text-white">
                  {user?.name
                    .split(" ")
                    .map((n) => n[0])
                    .join("")}
                </span>
              </div>
              <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-green-500 border-2 border-card" />
            </div>
            {!sidebarCollapsed && (
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-foreground truncate">
                  {user?.name}
                </div>
                <div className="text-xs text-muted-foreground capitalize">
                  {user?.role}
                </div>
              </div>
            )}
          </div>

          <button
            onClick={handleLogout}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-red-400 hover:bg-red-500/10 transition-all cursor-pointer ${
              sidebarCollapsed ? "justify-center" : ""
            }`}
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            {!sidebarCollapsed && <span>Sign Out</span>}
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col overflow-hidden w-full lg:w-auto">
        <header className="h-16 sm:h-20 border-b border-border/50 bg-card/80 backdrop-blur-md flex items-center px-4 sm:px-6 md:px-8">
          <div className="flex-1 max-w-2xl hidden md:block">
            <div className="relative group">
              <div className="absolute inset-0 bg-primary/10 rounded-lg blur-sm opacity-0 group-focus-within:opacity-100 transition-opacity" />
              <div className="relative flex items-center">
                <Search className="absolute left-3 sm:left-4 w-4 sm:w-5 h-4 sm:h-5 text-muted-foreground" />
                <input
                  type="search"
                  placeholder="Search incidents, knowledge base, or users..."
                  className="w-full pl-10 sm:pl-12 pr-4 py-2 sm:py-3 bg-muted/50 border border-border/50 rounded-lg text-sm focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all text-foreground placeholder:text-muted-foreground"
                />
                <div className="absolute right-4 hidden lg:flex items-center gap-1 text-xs text-muted-foreground">
                  <Command className="w-3 h-3" />
                  <span>K</span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-4 ml-4 sm:ml-6">
            {isConnected && (
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted/50 text-xs">
                <Wifi className="w-3 h-3 text-green-500" />
                <span className="text-green-500 font-medium">Live</span>
              </div>
            )}

            <ThemeToggle />

            <button className="md:hidden p-2 rounded-lg hover:bg-muted transition-colors cursor-pointer">
              <Search className="w-5 h-5 text-muted-foreground" />
            </button>

            <Link
              to="/dashboard/notifications"
              className="relative p-2 sm:p-2.5 rounded-lg hover:bg-muted transition-colors group"
            >
              <Bell className="w-5 h-5 text-muted-foreground group-hover:text-foreground transition-colors" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-5 h-5 px-1 bg-primary text-white text-xs font-bold rounded-full">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </Link>

            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="p-2 sm:p-2.5 rounded-lg hover:bg-muted transition-colors cursor-pointer"
              aria-label="Toggle menu"
            >
              <div className="flex flex-col gap-1">
                <div className="w-4 h-0.5 bg-muted-foreground rounded" />
                <div className="w-4 h-0.5 bg-muted-foreground rounded" />
                <div className="w-4 h-0.5 bg-muted-foreground rounded" />
              </div>
            </button>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto">{children}</main>
      </div>
    </div>
  );
}
